import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate, useParams } from 'react-router-dom';
import { MdAdd, MdDelete, MdArrowBack } from 'react-icons/md';

interface SubIndustry {
    name: string;
    sub?: string[];
}

const defaultPricing = [
    '10-15', '15-20', '20-25', '25-30', '30-35', '35-40',
    '40-45', '45-50', '50-55', '55-60', '60-65', '65-70',
    '70-75', '75-80', '80-85', '85-90', '90-95', '95-100',
];

export default function IndustryForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        forWho: '',
        pricing: defaultPricing,
        fields: [] as string[],
        mandatoryFields: [] as string[],
        steps: undefined as number | undefined,
        subIndustries: [] as SubIndustry[],
    });

    const [newField, setNewField] = useState('');
    const [newMandatoryField, setNewMandatoryField] = useState('');
    const [newSubIndustry, setNewSubIndustry] = useState('');
    const [newSubField, setNewSubField] = useState('');
    const [selectedSubIndex, setSelectedSubIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [availableFields, setAvailableFields] = useState<Array<{ id: string; name: string }>>([]);
    const [loadingFields, setLoadingFields] = useState(true);
    const [fieldSearchTerm, setFieldSearchTerm] = useState('');
    const [mandatoryFieldSearchTerm, setMandatoryFieldSearchTerm] = useState('');
    const [showFieldDropdown, setShowFieldDropdown] = useState(false);
    const [showMandatoryFieldDropdown, setShowMandatoryFieldDropdown] = useState(false);

    const fetchFormFields = async () => {
        try {
            setLoadingFields(true);
            const response = await axios.get(`${CONFIG.apiUrl}/form-field`);
            setAvailableFields(response.data.results || response.data.fields || []);
        } catch (error) {
            console.error('Error fetching form fields:', error);
            alert('Failed to load form fields');
        } finally {
            setLoadingFields(false);
        }
    };

    const fetchIndustry = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/industry/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormData(response.data);
        } catch (error) {
            console.error('Error fetching industry:', error);
            alert('Failed to load industry');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFormFields();
        if (isEdit) {
            fetchIndustry();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            alert('Industry name is required');
            return;
        }

        try {
            setSaving(true);
            const token = localStorage.getItem('token');
            
            if (isEdit) {
                await axios.put(`${CONFIG.apiUrl}/industry/${id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post(`${CONFIG.apiUrl}/industry`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            
            navigate('/back-office/industries');
        } catch (error: any) {
            console.error('Error saving industry:', error);
            alert(error.response?.data?.message || 'Failed to save industry');
        } finally {
            setSaving(false);
        }
    };

    const addField = () => {
        if (newField && !formData.fields.includes(newField)) {
            setFormData({ ...formData, fields: [...formData.fields, newField] });
            setNewField('');
        }
    };

    const removeField = (index: number) => {
        setFormData({ ...formData, fields: formData.fields.filter((_, i) => i !== index) });
    };

    const addMandatoryField = () => {
        if (newMandatoryField && !formData.mandatoryFields.includes(newMandatoryField)) {
            setFormData({ ...formData, mandatoryFields: [...formData.mandatoryFields, newMandatoryField] });
            setNewMandatoryField('');
        }
    };

    const removeMandatoryField = (index: number) => {
        setFormData({ ...formData, mandatoryFields: formData.mandatoryFields.filter((_, i) => i !== index) });
    };

    const addSubIndustry = () => {
        if (newSubIndustry.trim()) {
            setFormData({
                ...formData,
                subIndustries: [...formData.subIndustries, { name: newSubIndustry.trim(), sub: [] }]
            });
            setNewSubIndustry('');
        }
    };

    const removeSubIndustry = (index: number) => {
        setFormData({
            ...formData,
            subIndustries: formData.subIndustries.filter((_, i) => i !== index)
        });
    };

    const addSubField = (subIndex: number) => {
        if (newSubField.trim()) {
            const updated = [...formData.subIndustries];
            if (!updated[subIndex].sub) {
                updated[subIndex].sub = [];
            }
            updated[subIndex].sub!.push(newSubField.trim());
            setFormData({ ...formData, subIndustries: updated });
            setNewSubField('');
        }
    };

    const removeSubField = (subIndex: number, fieldIndex: number) => {
        const updated = [...formData.subIndustries];
        updated[subIndex].sub = updated[subIndex].sub?.filter((_, i) => i !== fieldIndex);
        setFormData({ ...formData, subIndustries: updated });
    };

    if (loading || loadingFields) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                </Card>
            </div>
        );
    }

    // Get available fields that are not already selected
    const availableFieldsForSelection = availableFields.filter(
        field => !formData.fields.includes(field.id)
    );
    const availableFieldsForMandatory = availableFields.filter(
        field => !formData.mandatoryFields.includes(field.id)
    );

    // Filter fields based on search term
    const filteredFieldsForSelection = availableFieldsForSelection.filter(
        field => field.name.toLowerCase().includes(fieldSearchTerm.toLowerCase())
    );
    const filteredFieldsForMandatory = availableFieldsForMandatory.filter(
        field => field.name.toLowerCase().includes(mandatoryFieldSearchTerm.toLowerCase())
    );

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/back-office/industries')}
                        className="rounded-lg bg-gray-200 p-2 text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        {isEdit ? 'Edit Industry' : 'Create Industry'}
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Basic Information
                        </h3>
                        
                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Industry Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                rows={3}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                For Who
                            </label>
                            <textarea
                                value={formData.forWho}
                                onChange={(e) => setFormData({ ...formData, forWho: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                rows={2}
                                placeholder="Target audience (e.g., agents, companies, etc.)"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Steps (optional)
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={formData.steps || ''}
                                onChange={(e) => setFormData({ ...formData, steps: e.target.value ? parseInt(e.target.value) : undefined })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Fields */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Fields
                        </h3>
                        
                        <div className="mb-3 flex gap-2">
                            <div className="relative flex-1">
                                <input
                                    type="text"
                                    value={fieldSearchTerm}
                                    onChange={(e) => setFieldSearchTerm(e.target.value)}
                                    onFocus={() => setShowFieldDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowFieldDropdown(false), 200)}
                                    placeholder="Search and select a field..."
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                                {showFieldDropdown && filteredFieldsForSelection.length > 0 && (
                                    <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                                        {filteredFieldsForSelection.map((field) => (
                                            <div
                                                key={field.id}
                                                onClick={() => {
                                                    setNewField(field.id);
                                                    setFieldSearchTerm(field.name);
                                                    setShowFieldDropdown(false);
                                                }}
                                                className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-navy-700 dark:text-white"
                                            >
                                                {field.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    addField();
                                    setFieldSearchTerm('');
                                }}
                                disabled={!newField}
                                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <MdAdd />
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {formData.fields.map((field, index) => (
                                <div key={index} className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1 text-sm dark:bg-navy-700">
                                    <span>{field}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeField(index)}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <MdDelete />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mandatory Fields */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Mandatory Fields
                        </h3>
                        
                        <div className="mb-3 flex gap-2">
                            <div className="relative flex-1">
                                <input
                                    type="text"
                                    value={mandatoryFieldSearchTerm}
                                    onChange={(e) => setMandatoryFieldSearchTerm(e.target.value)}
                                    onFocus={() => setShowMandatoryFieldDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowMandatoryFieldDropdown(false), 200)}
                                    placeholder="Search and select a mandatory field..."
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                                {showMandatoryFieldDropdown && filteredFieldsForMandatory.length > 0 && (
                                    <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                                        {filteredFieldsForMandatory.map((field) => (
                                            <div
                                                key={field.id}
                                                onClick={() => {
                                                    setNewMandatoryField(field.id);
                                                    setMandatoryFieldSearchTerm(field.name);
                                                    setShowMandatoryFieldDropdown(false);
                                                }}
                                                className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-navy-700 dark:text-white"
                                            >
                                                {field.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    addMandatoryField();
                                    setMandatoryFieldSearchTerm('');
                                }}
                                disabled={!newMandatoryField}
                                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <MdAdd />
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {formData.mandatoryFields.map((field, index) => (
                                <div key={index} className="flex items-center gap-2 rounded-lg bg-red-100 px-3 py-1 text-sm dark:bg-red-900">
                                    <span>{field}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeMandatoryField(index)}
                                        className="text-red-700 hover:text-red-900 dark:text-red-300"
                                    >
                                        <MdDelete />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Sub-Industries */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Sub-Industries
                        </h3>
                        
                        <div className="mb-3 flex gap-2">
                            <input
                                type="text"
                                value={newSubIndustry}
                                onChange={(e) => setNewSubIndustry(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubIndustry())}
                                placeholder="Add a sub-industry..."
                                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                            />
                            <button
                                type="button"
                                onClick={addSubIndustry}
                                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm text-white hover:bg-brand-600"
                            >
                                <MdAdd />
                            </button>
                        </div>

                        <div className="space-y-3">
                            {formData.subIndustries.map((subIndustry, subIndex) => (
                                <div key={subIndex} className="rounded-lg border border-gray-200 p-3 dark:border-navy-600">
                                    <div className="mb-2 flex items-center justify-between">
                                        <span className="font-medium text-navy-700 dark:text-white">
                                            {subIndustry.name}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeSubIndustry(subIndex)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <MdDelete />
                                        </button>
                                    </div>
                                    
                                    {selectedSubIndex === subIndex && (
                                        <div className="mb-2 flex gap-2">
                                            <input
                                                type="text"
                                                value={newSubField}
                                                onChange={(e) => setNewSubField(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubField(subIndex))}
                                                placeholder="Add sub-field..."
                                                className="flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => addSubField(subIndex)}
                                                className="rounded bg-green-500 px-2 py-1 text-xs text-white hover:bg-green-600"
                                            >
                                                Add
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedSubIndex(null)}
                                                className="rounded bg-gray-500 px-2 py-1 text-xs text-white hover:bg-gray-600"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    )}
                                    
                                    {subIndustry.sub && subIndustry.sub.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {subIndustry.sub.map((field, fieldIndex) => (
                                                <div key={fieldIndex} className="flex items-center gap-1 rounded bg-blue-100 px-2 py-1 text-xs dark:bg-blue-900">
                                                    <span>{field}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSubField(subIndex, fieldIndex)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={saving}
                            className="rounded-lg bg-brand-500 px-6 py-2 font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : isEdit ? 'Update Industry' : 'Create Industry'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/back-office/industries')}
                            className="rounded-lg bg-gray-300 px-6 py-2 font-medium text-gray-700 hover:bg-gray-400 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
